import { useEffect, useState } from 'react';
import { useCustomComponentContext } from './CustomComponentProvider';
import type { LoadedCustomComponentModule } from './hostRuntime';

export type CustomComponentLoadState =
  | { status: 'no-provider' }
  | { status: 'idle' }
  | { status: 'loading'; slug: string; sourceHash: string }
  | { status: 'ready'; slug: string; sourceHash: string; module: LoadedCustomComponentModule }
  | { status: 'error'; slug: string; error: Error };

/**
 * Editor-side artifact loader. The Suspense-driven publish/preview path uses `ctx.readResource`
 * directly; this hook is reserved for the editor canvas where we want independent per-slot
 * updates and `null`-while-loading semantics, NOT a Suspense boundary that could coordinate
 * sibling state across the canvas.
 *
 * Re-fires when either the slug or the hash changes (Yjs undo flips both atomically). Stale
 * responses are discarded with the `cancelled` flag so a fast edit-then-edit doesn't flash the
 * intermediate module. Returns `no-provider` when there's no `CustomComponentProvider` in the
 * tree — the renderer surfaces that as a clear placeholder without crashing.
 */
export function useCustomComponentModule(slug: string, sourceHash: string): CustomComponentLoadState {
  const ctx = useCustomComponentContext();
  const loadModuleByHash = ctx?.loadModuleByHash ?? null;
  const getPreloadedModule = ctx?.getPreloadedModule ?? null;

  const [state, setState] = useState<CustomComponentLoadState>(() => {
    if (!ctx) return { status: 'no-provider' };
    if (!sourceHash) return { status: 'idle' };
    const preloaded = getPreloadedModule?.(sourceHash) ?? null;
    if (preloaded) return { status: 'ready', slug, sourceHash, module: preloaded };
    return { status: 'loading', slug, sourceHash };
  });

  useEffect(() => {
    if (!ctx || !loadModuleByHash) {
      setState({ status: 'no-provider' });
      return () => undefined;
    }
    if (!sourceHash) {
      setState({ status: 'idle' });
      return () => undefined;
    }

    let cancelled = false;
    const preloaded = getPreloadedModule?.(sourceHash) ?? null;
    if (preloaded) {
      setState({ status: 'ready', slug, sourceHash, module: preloaded });
      return () => {
        cancelled = true;
      };
    }
    setState({ status: 'loading', slug, sourceHash });
    loadModuleByHash(sourceHash)
      .then((module) => {
        if (cancelled) return;
        setState({ status: 'ready', slug, sourceHash, module });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          slug,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [ctx, slug, sourceHash, loadModuleByHash, getPreloadedModule]);

  return state;
}
